/**
 * Detect whether the user is in an in-app browser (FB / Instagram / TikTok /
 * X / LinkedIn / Pinterest) and surface a hint to open the page in their
 * device's real browser.
 *
 * Motivating evidence (2026-05-24 prod Cloud Run logs):
 *   - 08:00 UTC: iPhone FB in-app browser  → POST /api/stytch/login-or-signup 500
 *   - yesterday 22:14 UTC (×2): Android FB in-app browser → /api/stytch/google 500
 * In-app browsers routinely break:
 *   - Google OAuth (consent popup blocked / cookies stripped)
 *   - Magic-link clicks (the link opens a NEW browser context that doesn't
 *     have the right cookie)
 *   - Stripe Checkout (some IABs sandbox payment elements)
 * The dominant H7 funnel leak (80% Google OAuth dropoff) probably has a
 * meaningful FB-IAB tail. A banner that nudges users to open the page in
 * Safari/Chrome converts the otherwise-lost cohort.
 *
 * UA patterns:
 *   - Facebook iOS:     FBAN/FBIOS / FBAV/...
 *   - Facebook Android: FB_IAB/FB4A
 *   - Instagram:        Instagram x.y.z
 *   - TikTok:           BytedanceWebview / Bytedance/Bytedancewebview
 *   - X / Twitter:      Twitter for iPhone / TwitterAndroid
 *   - LinkedIn:         LinkedInApp
 *   - Pinterest:        Pinterest
 */

export interface InAppBrowserInfo {
  isInAppBrowser: boolean;
  /** Friendly source label ("Facebook", "Instagram", etc.) when detected. */
  source: string | null;
  /** iOS users tap the ⋯ menu → "Open in Safari". Android varies. */
  platform: "ios" | "android" | "other";
}

const PATTERNS: { name: string; rx: RegExp }[] = [
  { name: "Facebook", rx: /FBAN|FB_IAB|FBAV/ },
  { name: "Instagram", rx: /Instagram/ },
  { name: "TikTok", rx: /BytedanceWebview|Bytedancewebview|musical_ly/i },
  { name: "X", rx: /Twitter for|TwitterAndroid/ },
  { name: "LinkedIn", rx: /LinkedInApp/ },
  { name: "Pinterest", rx: /Pinterest/ },
];

export function detectInAppBrowser(userAgent?: string): InAppBrowserInfo {
  const ua = typeof userAgent === "string"
    ? userAgent
    : typeof navigator !== "undefined"
      ? navigator.userAgent
      : "";

  let source: string | null = null;
  for (const p of PATTERNS) {
    if (p.rx.test(ua)) {
      source = p.name;
      break;
    }
  }

  let platform: "ios" | "android" | "other" = "other";
  if (/iPhone|iPad|iPod/i.test(ua)) platform = "ios";
  else if (/Android/i.test(ua)) platform = "android";

  return {
    isInAppBrowser: source !== null,
    source,
    platform,
  };
}
